import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { ChevronLeft, Clock3 } from "lucide-react";
import { useRootStore } from "@/state/RootStore";
import { useVisibleTests } from "@/hooks/useVisibleTests";
import LoadFailure from "../common/LoadFailure";
import LoadingSpinner from "../common/LoadingSpinner";
import dakotaGreat from "@/assets/images/dakota-great.webp";
import {
  NUM_SECTIONS,
  SECTION_ID_TO_PRACTICE_TEST_TYPE,
  TEST_TYPE_TO_RESULT_TITLE,
} from "../../../appsConfig";
import { getLocalizedPracticeTypeResultTitle } from "@/i18n/practiceTypeCopy";
import { ExamPerformanceCard } from "./ExamPerformanceCard";
import { ExamResultStreakCard } from "./ExamResultStreakCard";
import { getStudyStreak } from "@/utils/studyStreak";
import { useTranslation } from "react-i18next";
import { formatLong, formatTime } from "@/utils/date";

type ResultOrigin = "past-exams";

interface ResultLocationState {
  resultOrigin?: ResultOrigin;
}

const ExamResultStandalone = observer(() => {
  const { id: testId } = useParams();
  const store = useRootStore();
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const visibleTests = useVisibleTests();
  const hasDeleted = useRef(false);
  const resultOrigin =
    (location.state as ResultLocationState | null)?.resultOrigin === "past-exams"
      ? "past-exams"
      : undefined;
  const openedFromPastExams = resultOrigin === "past-exams";
  const redirectPath = store.mainAppStore.hasStudyAccess(true) ? "/study" : "/paywall";
  const missingResultRedirectPath = openedFromPastExams ? "/past-exams" : redirectPath;
  const testsStatus = store.testStore.testsStatus;
  const historyLoaded = testsStatus === "loaded";
  const testMissing = Boolean(testId) && !visibleTests.has(testId!);
  const test = testId ? visibleTests.get(testId) : undefined;

  useEffect(() => {
    if (historyLoaded && testMissing && !hasDeleted.current) {
      hasDeleted.current = true;
      navigate(missingResultRedirectPath, { replace: true });
    }
  }, [historyLoaded, missingResultRedirectPath, navigate, testMissing]);

  const handleRetryLoadTests = useCallback(() => {
    store.testStore.loadTests();
  }, [store]);

  const topics = useMemo(() => {
    if (!test) return [];
    return Array.from({ length: NUM_SECTIONS }, (_, index) => {
      const sectionId = index + 1;
      const total = test.numQuestions(sectionId);
      if (total <= 0) return null;
      const correct = test.numCorrect(sectionId);
      const practiceType = SECTION_ID_TO_PRACTICE_TEST_TYPE[sectionId];
      return {
        correct,
        score: Math.round((correct / total) * 100),
        title: getLocalizedPracticeTypeResultTitle(
          practiceType,
          TEST_TYPE_TO_RESULT_TITLE[practiceType],
        ) || `Section ${sectionId}`,
        total,
      };
    }).filter((topic): topic is NonNullable<typeof topic> => topic !== null);
  }, [
    store.questionsStore.deletedQuestions,
    store.questionsStore.questions,
    test,
  ]);

  if (testMissing) {
    if (testsStatus === "error") return <LoadFailure onRetry={handleRetryLoadTests} />;
    return <LoadingSpinner labelKey="results.loading_past_exams_alt" className="size-full" />;
  }

  if (!test || !testId) return null;

  const score = Math.round(test.percentCorrect() * 100);
  const passingScore = Math.round(store.questionsStore.getPassingScore(store.mainAppStore.selectedStateCode) * 100);
  const studyStreak = getStudyStreak(
    Array.from(store.testStore.tests.values())
      .filter((storedTest) => storedTest.isFinished)
      .map((storedTest) => (storedTest.endTime ?? storedTest.startTime).valueOf()),
  );
  const completedAt = test.endTime || test.startTime;
  const completedAtLabel = `${formatLong(completedAt, i18n.language)} • ${formatTime(completedAt, i18n.language)}`;
  const reviewQuestions = (questionIndex = 0) => {
    store.testStore.reviewTest(testId, false);
    navigate("/test", {
      state: {
        resultOrigin,
        reviewQuestionIndex: questionIndex,
        reviewResultId: testId,
      },
    });
  };

  return (
    <main className="flex h-dvh min-h-0 w-full overflow-hidden bg-white">
      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-20 shrink-0 items-center gap-3 border-b border-[#dddfe4] px-6">
          {openedFromPastExams && (
            <button
              aria-label={t("results.previous_results")}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#5b616d] outline-none hover:bg-[#f4f4f6] focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)]"
              onClick={() => navigate("/past-exams")}
              type="button"
            >
              <ChevronLeft aria-hidden="true" className="h-6 w-6" />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-[27px] text-[#0a0c11]">{t("results.exam_results_heading")}</h1>
            <p className="truncate text-sm leading-[21px] text-[#5b616d]">{t("results.completed_at", { date: completedAtLabel })}</p>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-auto bg-white">
          <div className="flex min-h-full items-center px-6 py-6">
            <div className="dp-page-container flex flex-col gap-6 xl:flex-row">
            <ExamResultStreakCard image={dakotaGreat} streakDays={studyStreak.days} streakEndsToday={studyStreak.endsToday} />
            <div className="flex min-w-0 flex-1 flex-col gap-6">
              <section className="flex min-h-[226px] flex-col rounded-[28px] bg-[var(--dp-brand-deep)] p-6 text-white">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-[#ffd135]">{score >= passingScore ? t("results.ready") : t("results.not_ready")}</span>
                  <span className="text-sm text-white/75">{t("results.momentum")}</span>
                </div>
                <div className="mt-5 flex items-center gap-4">
                  <strong className="text-5xl font-semibold leading-none text-[var(--dp-brand-button)]">{score}<span className="text-xl">%</span></strong>
                  <div>
                    <h2 className="text-lg font-semibold">{t("results.overall_score")}</h2>
                    <p className="text-sm text-white/70">{t("results.correct_summary", { correct: test.numCorrect(), total: test.numQuestions() })}</p>
                  </div>
                </div>
                <div className="relative mt-5 h-3 rounded-full bg-white/15">
                  <div className="h-full rounded-full bg-[var(--dp-brand-button)]" style={{ width: `${score}%` }} />
                  <span className="absolute -top-1 h-5 w-1 bg-[#f0b33e]" style={{ left: `${passingScore}%` }} />
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-white/75"><Clock3 className="h-4 w-4" />{t("results.per_answer", { time: test.averageAnswerTimeString })}</span>
                  <span className="font-semibold text-[#ffd135]">{t("results.to_pass", { score: passingScore })}</span>
                </div>
              </section>

              <section>
                <h2 className="mb-4 text-xl font-semibold leading-[30px] text-[#0a0c11]">{t("results.performance_breakdown")}</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {topics.map((topic) => <ExamPerformanceCard {...topic} key={topic.title} />)}
                </div>
              </section>

              <div className="mt-auto flex flex-wrap justify-end gap-3 pt-6 sm:gap-6">
                <button className="dp-button-secondary h-14 min-w-[220px] px-8 text-lg" onClick={() => reviewQuestions()} type="button">{t("results.review_questions")}</button>
                <button className="dp-button-primary h-14 min-w-[144px] px-8 text-lg" onClick={() => navigate(redirectPath)} type="button">{t("common.continue")}</button>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
});

export default ExamResultStandalone;
